import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  QueryNewNotificationsRequest,
  QueryNewNotificationsResponse,
  ViewAllNotificationsRequest,
  ViewAllNotificationsResponse,
  ViewNewNotificationsRequest,
  ViewNewNotificationsResponse
} from './notification.types';

import {
  requestQueryNewNotifications,
  requestViewAllNotifications,
  requestViewNewNotifications
} from './notification.actions';

import {
  getQueryNewNotificationsRequest,
  getViewAllNotificationsRequest,
  getViewNewNotificationsRequest
} from './notification.endpoints';

import {
  addSystemNotice,
  prependNotifications,
  setNewNotifications,
  setNotifications
} from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performViewAllNotificationsRequest({ payload }: SagaPayload<ViewAllNotificationsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewAllNotificationsRequest(requestData);

    const { data }: AxiosResponse<ViewAllNotificationsResponse> = yield call(axios, endpoint, options);

    yield put(setNotifications(data.notifications));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewAllNotificationsRequest() {
  yield takeLatest(requestViewAllNotifications.type, performViewAllNotificationsRequest);
}

export function* performQueryNewNotificationsRequest({ payload }: SagaPayload<QueryNewNotificationsRequest, boolean>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getQueryNewNotificationsRequest(requestData);

    const { data }: AxiosResponse<QueryNewNotificationsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.areNewNotifications);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForQueryNewNotificationsRequest() {
  yield takeLatest(requestQueryNewNotifications.type, performQueryNewNotificationsRequest);
}

export function* performViewNewNotificationsRequest({ payload }: SagaPayload<ViewNewNotificationsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewNewNotificationsRequest(requestData);

    const { data }: AxiosResponse<ViewNewNotificationsResponse> = yield call(axios, endpoint, options);

    yield put(setNewNotifications(true));
    yield put(prependNotifications(data.newNotifications));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewNewNotificationsRequest() {
  yield takeLatest(requestViewNewNotifications.type, performViewNewNotificationsRequest);
}

export default function* notificationSaga() {
  yield all([
    watchForViewAllNotificationsRequest(),
    watchForQueryNewNotificationsRequest(),
    watchForViewNewNotificationsRequest()
  ]);
}
